'use client';

import { useState, useEffect } from 'react';
import { FiSend, FiLoader, FiTerminal, FiActivity } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { sendEmail } from '@/app/actions/send-email';
import Script from 'next/script'; // Use Next.js optimized Script component

interface ContactFormProps {
  recaptchaSiteKey: string;
}

export default function ContactForm({ recaptchaSiteKey }: ContactFormProps) {
  const [loading, setLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Debug: Log if the key is missing on mount
  useEffect(() => {
    if (!recaptchaSiteKey) {
      console.error("CRITICAL: recaptchaSiteKey is undefined. Check your environment variables.");
    }
  }, [recaptchaSiteKey]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // 1. Check if reCAPTCHA is available
    if (!(window as any).grecaptcha) {
      toast.error('SECURITY SYSTEM INITIALIZING... PLEASE TRY AGAIN');
      return;
    }

    setLoading(true);
    console.log("🚀 Transmission initialized...");

    try {
      const target = e.currentTarget;
      const formData = new FormData(target);

      // 2. Execute reCAPTCHA and wait for token
      const token = await (window as any).grecaptcha.execute(recaptchaSiteKey, {
        action: 'submit',
      });

      if (!token) {
        throw new Error("RECAPTCHA_TOKEN_EMPTY");
      }

      formData.append('recaptchaToken', token);
      console.log("🛡️ Security token verified. Dispatching payload...");

      // 3. Trigger Server Action
      const result = await sendEmail(formData);

      if (result.success) {
        toast.success('TRANSMISSION LOGGED IN REGISTRY', {
          className: 'bg-zinc-900 text-white font-mono text-[10px] border border-indigo-500',
        });
        target.reset();
      } else {
        console.error("❌ Server Error:", result.error);
        toast.error(result.error || 'UPLINK FAILURE');
      }
    } catch (err) {
      console.error("🔥 Critical Error during submission:", err);
      toast.error('CRITICAL SYSTEM ERROR');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Load reCAPTCHA script using Next.js Script strategy */}
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`}
        onLoad={() => {
          setIsScriptLoaded(true);
          console.log("🛡️ reCAPTCHA script loaded successfully.");
        }}
      />

      <form
        onSubmit={handleSubmit}
        className="relative p-8 md:p-12 rounded-[3rem] bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 shadow-2xl space-y-8"
      >
        {/* Tactical Header */}
        <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-900 pb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-500/10 rounded-2xl text-indigo-600">
              <FiTerminal size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Secure Uplink</h2>
              <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-[0.2em]">Protocol: encrypted_transmission</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full">
            <FiActivity className={`text-emerald-500 ${isScriptLoaded ? 'animate-pulse' : 'opacity-20'}`} size={12} />
            <span className="text-[8px] font-black text-emerald-600 uppercase">
              {isScriptLoaded ? 'System Live' : 'Initializing...'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 ml-2">Identity_Origin</label>
            <input name="name" required type="text" placeholder="NAME / ORGANIZATION" className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 ml-2">Return_Coordinates</label>
            <input name="email" required type="email" placeholder="EMAIL ADDRESS" className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none transition-all" />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 ml-2">Mission_Sector</label>
          <select name="subject" className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-2xl text-[10px] font-black uppercase tracking-widest focus:ring-2 focus:ring-indigo-500 outline-none appearance-none cursor-pointer">
            <option value="Inquiry">Project Intelligence</option>
            <option value="Consultation">Technical Strategy</option>
            <option value="General">General Transmission</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 ml-2">Payload_Content</label>
          <textarea name="message" required rows={4} placeholder="ENTER DATA..." className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-3xl text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all resize-none" />
        </div>

        <button
          disabled={loading || !isScriptLoaded}
          className="w-full py-6 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-[1.5rem] font-black text-[10px] uppercase tracking-[0.4em] transition-all flex items-center justify-center gap-4 hover:gap-6 active:scale-[0.98] disabled:opacity-50 shadow-2xl"
        >
          {loading ? (
            <>BROADCASTING... <FiLoader className="animate-spin" /></>
          ) : (
            <>Execute Transmission <FiSend size={16}/></>
          )}
        </button>

        <p className="text-center text-[8px] text-zinc-500 font-mono uppercase tracking-widest">
          Protected by Google reCAPTCHA // Verified Secure
        </p>
      </form>
    </>
  );
}